import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Stat, StatLabel, StatNumber, StatHelpText,
  Badge, Icon, Alert, AlertIcon, Button, Link,
} from '@chakra-ui/react';
import {
  FiBox, FiLayers, FiServer, FiCpu, FiRefreshCw, FiExternalLink,
  FiHardDrive, FiGlobe, FiActivity, FiRepeat,
} from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function ClusterOverview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/overview/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => { setOverview(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return (
    <Alert status="error" borderRadius="lg">
      <AlertIcon />
      <VStack align="start" spacing={1}>
        <Text fontWeight="bold">Failed to connect to cluster</Text>
        <Text fontSize="sm">{error}</Text>
      </VStack>
    </Alert>
  );

  const s = overview?.summary || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Cluster Overview</Heading>
          <Text color="gray.500">
            Namespace: <Badge colorScheme="blue" fontSize="sm">{overview?.namespace}</Badge>
          </Text>
        </VStack>
        <HStack>
          {overview?.traffic_manager_url && (
            <Link href={overview.traffic_manager_url} isExternal>
              <Button leftIcon={<FiExternalLink />} variant="outline" colorScheme="blue">
                Traffic Manager
              </Button>
            </Link>
          )}
          <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchOverview}>Refresh</Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="blue.400">
          <HStack mb={3}>
            <Icon as={FiBox} boxSize={6} color="blue.500" />
            <Text fontWeight="bold" color="gray.600">Pods</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.running_pods || 0}</StatNumber>
            <StatHelpText>
              {s.running_pods || 0} running / {s.total_pods || 0} total
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="green.400">
          <HStack mb={3}>
            <Icon as={FiLayers} boxSize={6} color="green.500" />
            <Text fontWeight="bold" color="gray.600">Deployments</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.healthy_deployments || 0}</StatNumber>
            <StatHelpText>
              {s.healthy_deployments || 0} healthy / {s.total_deployments || 0} total
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="purple.400">
          <HStack mb={3}>
            <Icon as={FiServer} boxSize={6} color="purple.500" />
            <Text fontWeight="bold" color="gray.600">Services</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_services || 0}</StatNumber>
            <StatHelpText>Active services</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="orange.400">
          <HStack mb={3}>
            <Icon as={FiCpu} boxSize={6} color="orange.500" />
            <Text fontWeight="bold" color="gray.600">Nodes</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_nodes || 0}</StatNumber>
            <StatHelpText>Cluster nodes</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor={s.volumes_over_80 ? 'red.400' : 'cyan.400'}>
          <HStack mb={3}>
            <Icon as={FiHardDrive} boxSize={6} color={s.volumes_over_80 ? 'red.500' : 'cyan.500'} />
            <Text fontWeight="bold" color="gray.600">Persistent Volumes</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_volumes || 0}</StatNumber>
            <StatHelpText>
              {s.bound_volumes || 0} bound
              {s.volumes_over_80 ? ` · ${s.volumes_over_80} >80% full` : ''}
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="teal.400">
          <HStack mb={3}>
            <Icon as={FiGlobe} boxSize={6} color="teal.500" />
            <Text fontWeight="bold" color="gray.600">Ingresses</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_ingresses || 0}</StatNumber>
            <StatHelpText>Routed hosts</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor={s.warning_events ? 'red.400' : 'gray.300'}>
          <HStack mb={3}>
            <Icon as={FiActivity} boxSize={6} color={s.warning_events ? 'red.500' : 'gray.400'} />
            <Text fontWeight="bold" color="gray.600">Events</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_events || 0}</StatNumber>
            <StatHelpText>
              {s.warning_events || 0} warnings (recent)
            </StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor={s.failed_cronjobs ? 'red.400' : 'pink.400'}>
          <HStack mb={3}>
            <Icon as={FiRepeat} boxSize={6} color={s.failed_cronjobs ? 'red.500' : 'pink.500'} />
            <Text fontWeight="bold" color="gray.600">CronJobs</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{s.total_cronjobs || 0}</StatNumber>
            <StatHelpText>
              {s.failed_cronjobs || 0} failed last run
            </StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default ClusterOverview;
